import type { MixSoundId } from '../components/landing/sleep/soundMixActiveSounds';

/** 백엔드와 동일한 샘플 레코드(하드코딩) — `title`으로 재생 URL을 매칭한다. */
export type HardcodedMixSoundEntry = {
  id: string;
  type: string;
  status: string;
  isPaid: boolean;
  soundUrl: string;
  iconUrl: string;
  duration: number;
  createdAt: string;
  updatedAt: string;
  targetLocale: string;
  title: string;
  description: string;
};

export const HARDCODED_MIX_SOUND_LIST: readonly HardcodedMixSoundEntry[] = [
  {
    id: '06a7064b-fc43-4a6e-8487-58cdff5022d0',
    type: 'environment',
    status: 'active',
    isPaid: true,
    soundUrl: 'sounds/mix-sounds/environment/0c8d0198-3bce-4e2f-8945-b98b9bd2c02a.mpga',
    iconUrl: 'images/mix-sounds/environment/47852f95-ad2d-4f54-be91-c81b88a1dd7b.png',
    duration: 4156,
    createdAt: '2025-09-08T00:15:59.000Z',
    updatedAt: '2025-09-08T00:16:04.000Z',
    targetLocale: 'ko',
    title: '비행기',
    description: '',
  },
  {
    id: '454982de-37d6-46ce-8ee6-97efd732e637',
    type: 'environment',
    status: 'active',
    isPaid: true,
    soundUrl: 'sounds/mix-sounds/environment/6aa17ae0-db39-4ca2-a327-b1094ab583ca.mpga',
    iconUrl: 'images/mix-sounds/environment/9c806de8-5ddd-42ea-91a2-d666a906cf0a.png',
    duration: 2118,
    createdAt: '2025-09-08T00:15:10.000Z',
    updatedAt: '2025-09-08T00:15:10.000Z',
    targetLocale: 'ko',
    title: '바람',
    description: '',
  },
  {
    id: '53533b84-cbde-4d19-a598-db5cf6991855',
    type: 'environment',
    status: 'active',
    isPaid: true,
    soundUrl: 'sounds/mix-sounds/frequency/6dd24259-7163-4029-900d-3bdf8676f387.mpga',
    iconUrl: 'images/mix-sounds/frequency/4f6e1bc1-c250-4f76-9c44-e3591382ff55.png',
    duration: 3920,
    createdAt: '2025-09-08T00:12:54.000Z',
    updatedAt: '2025-09-08T00:14:09.000Z',
    targetLocale: 'ko',
    title: '파도',
    description: '',
  },
  {
    id: 'bb911831-56d2-4735-ab56-04beaeaf6257',
    type: 'environment',
    status: 'active',
    isPaid: true,
    soundUrl: 'sounds/mix-sounds/frequency/03d3ab96-e425-4c9b-9e2c-fc9d5b6ea2aa.mpga',
    iconUrl: 'images/mix-sounds/frequency/fb1b12be-bc9a-4bd7-a5a0-2af139ef200d.png',
    duration: 1920,
    createdAt: '2025-09-08T00:12:08.000Z',
    updatedAt: '2025-09-08T00:14:06.000Z',
    targetLocale: 'ko',
    title: '계곡 물',
    description: '',
  },
  {
    id: '3abb9917-4552-4133-b539-7c61d734014f',
    type: 'environment',
    status: 'active',
    isPaid: false,
    soundUrl: 'sounds/mix-sounds/environment/a93d21e2-9e9c-45c0-915b-6ef97e63764a.mpga',
    iconUrl: 'images/mix-sounds/environment/a44dab22-d52e-4711-a21c-dcac87f5eec3.png',
    duration: 629,
    createdAt: '2025-09-08T00:11:32.000Z',
    updatedAt: '2025-09-08T00:11:37.000Z',
    targetLocale: 'ko',
    title: '새소래',
    description: '',
  },
  {
    id: '8db38489-d28c-4d07-90d8-3c7fa1448c08',
    type: 'environment',
    status: 'active',
    isPaid: true,
    soundUrl: 'sounds/mix-sounds/environment/66682865-74f3-4a39-9b9b-9131b2286e3f.mpga',
    iconUrl: 'images/mix-sounds/environment/fc7762e4-a092-4254-9e28-05e5122dd674.png',
    duration: 3600,
    createdAt: '2025-09-08T00:10:26.000Z',
    updatedAt: '2025-09-08T00:10:26.000Z',
    targetLocale: 'ko',
    title: '빗소리',
    description: '',
  },
];

/** `MixSoundId` ↔ 하드코딩 리스트의 `title`(문자열 정확 일치). 없으면 번들 폴백만 사용. */
const MIX_SOUND_ID_TO_LIST_TITLE: Partial<Record<MixSoundId, string>> = {
  rain: '빗소리',
  bird: '새소래',
  flight: '비행기',
  wind: '바람',
  valley: '계곡 물',
};

/**
 * - `development` 또는 `REACT_APP_MIX_SOUND_SAME_ORIGIN=1`: `/${path}` → **페이지와 동일 출처**
 *   (CRA dev는 `setupProxy`가 `/sounds` → CDN. 배포 시에는 CDN/리버스 프록시에서 `/sounds`를 에셋으로 넘겨야 함)
 * - 그 외(production 기본): `REACT_APP_IMG_ENDPOINT` 직접 URL (교차 출처 — Safari 등에서 `audio.volume` 조절이 무시될 수 있음)
 */
function resolveMixSoundPublicUrl(relativePath: string): string {
  const trimmed = relativePath.trim().replace(/^\//, '');
  const useSameOriginPlayback =
    process.env.NODE_ENV === 'development' || process.env.REACT_APP_MIX_SOUND_SAME_ORIGIN === '1';

  if (useSameOriginPlayback) {
    return `/${trimmed}`;
  }

  const base = (process.env.REACT_APP_IMG_ENDPOINT || '').replace(/\/$/, '');
  if (!base) {
    return trimmed.startsWith('http') ? trimmed : `/${trimmed}`;
  }
  return `${base}/${trimmed}`;
}

/**
 * 하드코딩 `HARDCODED_MIX_SOUND_LIST`를 `title`로 조회해 `MixSoundId`별 절대 음원 URL을 만든다.
 */
export function getHardcodedMixSoundSrcById(): Partial<Record<MixSoundId, string>> {
  const titleToUrl = new Map<string, string>();
  for (const row of HARDCODED_MIX_SOUND_LIST) {
    const t = row.title.trim();
    if (t) titleToUrl.set(t, resolveMixSoundPublicUrl(row.soundUrl));
  }

  const out: Partial<Record<MixSoundId, string>> = {};
  const ids = Object.keys(MIX_SOUND_ID_TO_LIST_TITLE) as MixSoundId[];
  for (const id of ids) {
    const title = MIX_SOUND_ID_TO_LIST_TITLE[id];
    if (!title) continue;
    const url = titleToUrl.get(title);
    if (url) out[id] = url;
  }
  return out;
}
